import { type ContactableProfileRole, ProfileRole, type TeamProfileRole } from "@qendo/profile";
import { useMemo } from "react";
import { Text } from "react-native";
import { type RadioGroupProps, type RadioOption, RadioSelector } from "~/components/form/radio/RadioSelector";
import { ChangeText } from "~/components/layout/text";
import { useProfile } from "~/providers/ProfileProvider";

type ContactType<T extends boolean> = T extends true ? ContactableProfileRole : TeamProfileRole;

export type TeamRoleSelectorProps<T extends boolean> = {
    withContact?: T;
} & Omit<RadioGroupProps<ContactType<T>>, "options" | "columns" | "title">;

export const TeamRoleSelector = ({ withContact, ...selectorProps }: TeamRoleSelectorProps<boolean>) => {
    const { canUpdateRole } = useProfile();

    const options = useMemo(() => {
        const options: RadioOption<ContactType<false>>[] = [
            {
                value: ProfileRole.owner,
                label: "Owner",
                subtitle: "Edit access to your entire account.",
                disabled: !canUpdateRole(ProfileRole.owner),
            },
            {
                value: ProfileRole.support,
                label: "Support",
                subtitle: (
                    <Text>
                        Read-only access to your entire account.
                        <ChangeText size="xs" decoration="italic">
                            {"\n\n"}Recommended for partners and family.
                        </ChangeText>
                    </Text>
                ),
            },
            {
                value: ProfileRole.medical,
                label: "Medical",
                subtitle: (
                    <Text>
                        Read-only access to your health profile and journals (excludes calendar events).
                        <ChangeText size="xs" decoration="italic">
                            {"\n\n"}Recommended for medical professionals.
                        </ChangeText>
                    </Text>
                ),
            },
        ];

        if (withContact) {
            return [
                ...options,
                {
                    value: ProfileRole.contact,
                    label: "Contact",
                    subtitle: (
                        <Text>
                            No access to your QENDO profile.
                            <ChangeText size="xs" decoration="italic">
                                {"\n\n"}Recommended for other contacts.
                            </ChangeText>
                        </Text>
                    ),
                },
            ] as RadioOption<ContactType<true>>[];
        }

        return options;
    }, [canUpdateRole, withContact]);

    return <RadioSelector {...selectorProps} title="Select an access level" options={options} />;
};
